import { z } from 'zod';
import { DiagnosticSummary } from '@/diagnostic/domain/DiagnosticSummary.ts';
import { Optional } from '@/generic/functional';
import { Duration } from '@/diagnostic/domain/Duration.ts';
import { type Page, jsonToPage, pageSchemaOf } from '@/generic/pagination';

const metricsSchema = z.object({
  durationMilliseconds: z.number(),
  estimatedCostUsd: z.number(),
});

const diagnosticSummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  generatedAt: z.string(),
  metrics: metricsSchema,
  linkedToOrganization: z.boolean(),
});

export type DiagnosticSummaryJson = z.infer<typeof diagnosticSummarySchema>;

const diagnosticsSchema = pageSchemaOf(diagnosticSummarySchema);

export type DiagnosticsSummaryJson = z.infer<typeof diagnosticsSchema>;

const jsonToDiagnosticSummary = (json: DiagnosticSummaryJson): DiagnosticSummary =>
  DiagnosticSummary.of({
    id: json.id,
    name: Optional.of(json.name),
    cost: json.metrics.estimatedCostUsd,
    date: json.generatedAt,
    duration: Duration.fromMilliseconds(json.metrics.durationMilliseconds),
    linkedToOrganization: json.linkedToOrganization,
  });

const jsonToDiagnosticsSummary = (json: DiagnosticsSummaryJson): DiagnosticSummary[] =>
  json.content.map(jsonToDiagnosticSummary);

export const parseJsonToDiagnosticsSummary = (json: unknown): DiagnosticSummary[] =>
  jsonToDiagnosticsSummary(diagnosticsSchema.parse(json));

export const parseJsonToDiagnosticsSummaryPage = (json: unknown): Page<DiagnosticSummary> =>
  jsonToPage(diagnosticsSchema.parse(json), jsonToDiagnosticSummary);
